"use client";

import React, { useState } from "react";
import { CalendarEvent } from "../../types/calendar";
import { Button } from "../button";
import {
  UserCheck,
  MessageSquare,
  Mail,
  Building2,
  Users,
  Zap,
} from "lucide-react";
import { Badge } from "../badge";
import { EventFormSection } from "./calendarinvites/shared/EventFormShell";

interface EventCRMTabProps {
  event: CalendarEvent;
  onLogActivity?: (activityData: any) => Promise<void>;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  isPageView?: boolean;
}

// Collapse long attendee lists: show this many, then "show more".
const ATTENDEE_PREVIEW_COUNT = 6;

export function EventCRMTab({
  event,
  onLogActivity,
  onComposeEmail,
}: EventCRMTabProps) {
  const [isLoggingActivity, setIsLoggingActivity] = useState(false);
  const [showAllAttendees, setShowAllAttendees] = useState(false);

  // Extract unique email addresses from event
  const getAllEventEmails = () => {
    const emails = new Set<string>();

    if (event.organizer) {
      emails.add(event.organizer);
    }

    if (event.attendees) {
      event.attendees.forEach((email) => emails.add(email));
    }

    return Array.from(emails);
  };

  const handleLogActivity = async () => {
    if (!onLogActivity) return;

    setIsLoggingActivity(true);
    try {
      // Pre-fill activity data with event information
      const activityData = {
        type: "meeting",
        title: `Meeting: ${event.title}`,
        description: event.description || "",
        date: event.start,
        location: event.location,
        attendees: getAllEventEmails(),
        eventId: event.id,
      };

      await onLogActivity(activityData);
    } catch (error) {
      console.error("Failed to log activity:", error);
    } finally {
      setIsLoggingActivity(false);
    }
  };

  const handleComposeEmail = () => {
    if (!onComposeEmail) return;

    const recipients = getAllEventEmails();
    const subject = `Re: ${event.title}`;

    onComposeEmail(recipients, subject);
  };

  const allEmails = getAllEventEmails();

  const attendees = event.attendees ?? [];
  const visibleAttendees = showAllAttendees
    ? attendees
    : attendees.slice(0, ATTENDEE_PREVIEW_COUNT);
  const hiddenCount = attendees.length - visibleAttendees.length;

  return (
    <div className="space-y-6">
      {/* Quick CRM Actions */}
      {(onLogActivity || (onComposeEmail && allEmails.length > 0)) && (
        <EventFormSection title="CRM actions" icon={Zap}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Log Activity Button */}
            {onLogActivity && (
              <Button
                type="button"
                onClick={handleLogActivity}
                disabled={isLoggingActivity}
                className="flex h-auto flex-col items-center space-y-2 border-purple-200 bg-purple-50 py-4 text-purple-700 hover:bg-purple-100"
                variant="outline"
              >
                <MessageSquare className="h-5 w-5" />
                <div className="text-center">
                  <div className="text-sm font-medium">
                    {isLoggingActivity ? "Logging..." : "Log Activity"}
                  </div>
                  <div className="mt-1 text-xs text-purple-600">
                    Record this meeting in CRM
                  </div>
                </div>
              </Button>
            )}

            {/* Compose Email Button */}
            {onComposeEmail && allEmails.length > 0 && (
              <Button
                type="button"
                onClick={handleComposeEmail}
                className="flex h-auto flex-col items-center space-y-2 border-blue-200 bg-blue-50 py-4 text-blue-700 hover:bg-blue-100"
                variant="outline"
              >
                <Mail className="h-5 w-5" />
                <div className="text-center">
                  <div className="text-sm font-medium">Compose Email</div>
                  <div className="mt-1 text-xs text-blue-600">
                    Email all attendees
                  </div>
                </div>
              </Button>
            )}
          </div>
        </EventFormSection>
      )}

      {/* Contacts */}
      <EventFormSection title="Contacts" icon={Users}>
        {/* Event Organizer */}
        {event.organizer && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Organizer</h4>
            <ContactCard
              email={event.organizer}
              role="organizer"
              onComposeEmail={onComposeEmail}
            />
          </div>
        )}

        {/* Event Attendees — 2-col grid on lg+, collapsed when long */}
        {attendees.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">
              Attendees ({attendees.length})
            </h4>
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {visibleAttendees.map((email, index) => (
                <ContactCard
                  key={index}
                  email={email}
                  role="attendee"
                  onComposeEmail={onComposeEmail}
                />
              ))}
            </div>
            {hiddenCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAllAttendees(true)}
                className="text-xs"
              >
                Show {hiddenCount} more
              </Button>
            )}
            {showAllAttendees && attendees.length > ATTENDEE_PREVIEW_COUNT && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAllAttendees(false)}
                className="text-xs"
              >
                Show fewer
              </Button>
            )}
          </div>
        )}

        {!event.organizer && attendees.length === 0 && (
          <p className="text-sm text-muted-foreground">No contacts on this event.</p>
        )}
      </EventFormSection>
    </div>
  );
}

// Contact Card Component with CRM Integration
interface ContactCardProps {
  email: string;
  role: "organizer" | "attendee";
  onComposeEmail?: (recipients: string[], subject?: string) => void;
}

function ContactCard({ email, role, onComposeEmail }: ContactCardProps) {
  const extractName = (email: string) => {
    const name = email.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const extractCompanyFromEmail = (email: string) => {
    const domain = email.split('@')[1];
    if (!domain) return null;

    // Extract company name from domain (remove .com, .org, etc.)
    const companyName = domain.split('.')[0];
    return companyName.charAt(0).toUpperCase() + companyName.slice(1);
  };

  const displayName = extractName(email);
  const companyName = extractCompanyFromEmail(email);

  return (
    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors">
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
          <span className="text-blue-600 text-xs font-medium">
            {displayName.charAt(0)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground truncate">{email}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge
              variant={role === "organizer" ? "default" : "secondary"}
              className="text-xs"
            >
              {role}
            </Badge>
            {companyName && (
              <Badge variant="outline" className="text-xs">
                {companyName}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-1 shrink-0">
        {/* Contact Details Button - placeholder for ContactPopover integration */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          title="View contact details"
          onClick={() => {
            // This is a placeholder - in the consultanttickets app, this would be wrapped with ContactPopover
            console.log("Contact details for:", email);
          }}
        >
          <UserCheck className="h-4 w-4" />
        </Button>

        {/* Company Details Button */}
        {companyName && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            title={`View ${companyName} contacts`}
            onClick={() => {
              // This is a placeholder - in the consultanttickets app, this would navigate to company page
              console.log("Company details for:", companyName);
              if (typeof window !== 'undefined') {
                window.open(`/companies/${encodeURIComponent(companyName)}`, '_blank');
              }
            }}
          >
            <Building2 className="h-4 w-4" />
          </Button>
        )}

        {onComposeEmail && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onComposeEmail([email])}
            className="h-8 w-8 p-0"
            title="Send email"
          >
            <Mail className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
